

import { makeStyles } from 'tss-react/mui';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  ContentTemplate,
  Grid,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TableState,
  useDataTable,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { candidateService } from '../../../api';
import {
  CreateCheckEmailEligibilityResponse,
  RawCandidateFunctionRequest,
} from '../../../api/candidate/iexam-create/@opadmin/models';
import { getIndexValue } from '../../../utils/ArrayUtils';
import EditDetailsModal from '../../../components/modals/EditDetailsModal';
import IExamCheckEmailModal from './IExamCheckEmailModal';
import { PaginatedData } from '../../../models/List';
import { RawCandidateItemResponse } from '../../../models/RawCandidate';
import { FileUploadState } from '../../../models/File';
import { RetrieveS3URLResponse } from '../../../api/document/files/api-services/model';
import DownloadAuditProgressModal from '../../../components/modals/DownloadAuditProgressModal';
import { IExamCandidateCreateDataTable } from './IExamCandidateCreateDataTable';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';

const useStyles = makeStyles()((theme) =>
({
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  searchInput: {
    maxWidth: '100%',
  },
}));

const useCandidateCreateAPIs = (
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>
) => {
  const {
    _refetch,
    isLoading,
    data,
    // isSuccess: eligibleSuccess,
    // isError: eligibleError,
  } = candidateService.iexamCreate.opadmin.usePostRawCandidateSearch(
    {
      order: state.order,
      sort: state.sortColumn,
      page: state.page,
      pageSize: state.size,
    },
    {
      candidateType: getIndexValue(state.filters.candidateType, 0),
      candidateSchool: state.filters.school || [],
      examLevel: getIndexValue(state.filters.examLevel, 0),
      authenticationType: getIndexValue(state.filters.authentication, 0),
      citizenship: getIndexValue(state.filters.citizenship, 0),
      search: state.search ?? '',
    },
    {
      enabled: false,
      onSuccess: () => null,
      onError: () => null,
    }
  );

  useEffect(() => {
    if (data) {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.data.content,
          count: data.data.totalElements,
        },
      } as TableActionTypes);
    }
  }, [data, tableDispatch]);

  const mutateSuccess = useCallback(() => {
    tableDispatch({
      type: 'CLEAR_SELECTION',
    } as TableActionTypes);
    _refetch();
  }, [_refetch, tableDispatch]);
  const mutateFail = useCallback(() => null, []);

  const generateMutation = candidateService.iexamCreate.opadmin.usePostCandidateCreateGenerate(
    mutateSuccess,
    mutateFail
  );

  const {
    numOfNoEmailCandidates,
    numOfBlockedCandidates,
    showCheckEligibilityModal,
    setShowCheckEligibilityModal,
    disabledEmailButton,
    callCheckEmailMutate,
    callSendEmailMutate,
    modalType,
  } = useCheckEligibilityAndSendEmail(data, mutateSuccess, mutateFail);

  const {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  } = useExportDownloadAction(data);

  return {
    _refetch,
    isLoading,
    generateMutation,
    numOfNoEmailCandidates,
    numOfBlockedCandidates,
    showCheckEligibilityModal,
    setShowCheckEligibilityModal,
    disabledEmailButton,
    callCheckEmailMutate,
    callSendEmailMutate,
    modalType,
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  };
};

const useCheckEligibilityAndSendEmail = (
  data: AxiosResponse<PaginatedData<RawCandidateItemResponse>> | undefined,
  mutateSuccess: () => void,
  mutateFail: () => void
) => {
  const [numOfNoEmailCandidates, setNumOfNoEmailCandidates] = useState(0);
  const [numOfBlockedCandidates, setNumOfBlockedCandidates] = useState(0);
  const [showCheckEligibilityModal, setShowCheckEligibilityModal] = useState(false);
  const [fireSendEmail, setFireSendEmail] = useState(false);
  const [modalType, setModalType] = useState<'ALL' | 'SELECTED' | 'INDIVIDUAL'>('ALL');
  const [emailRequest, setEmailRequest] = useState<RawCandidateFunctionRequest>({
    uuid: [],
    candidateCreationSearchParamModel: {
      candidateType: '',
      candidateSchool: [],
      examLevel: '',
      authenticationType: getIndexValue([], 0),
      citizenship: '',
    },
  });
  const [totalNumberOfCandidates, setTotalNumberOfCandidates] = useState(0);

  const emailMutation = candidateService.iexamCreate.opadmin.usePostCandidateCreateSendEmail(
    (res: AxiosResponse<undefined>) => {
      mutateSuccess();
      setShowCheckEligibilityModal(false);
    },
    mutateFail
  );

  const callSendEmailMutate = useCallback(() => {
    setFireSendEmail(true);
  }, [setFireSendEmail]);

  const { mutate: emailMutate, isLoading: emailLoading } = emailMutation;
  useEffect(() => {
    if (fireSendEmail) {
      if (
        totalNumberOfCandidates === 0 ||
        totalNumberOfCandidates > numOfNoEmailCandidates + numOfBlockedCandidates
      ) {
        emailMutate(emailRequest);
      } else {
        setShowCheckEligibilityModal(false);
      }
      setFireSendEmail(false);
    }
  }, [
    fireSendEmail,
    emailMutate,
    emailRequest,
    totalNumberOfCandidates,
    numOfNoEmailCandidates,
    numOfBlockedCandidates,
  ]);

  const checkEmailEligibilityCallback = useCallback(
    (res: CreateCheckEmailEligibilityResponse) => {
      setNumOfNoEmailCandidates(res.numOfNoEmailCandidate);
      setNumOfBlockedCandidates(res.numOfBlockedCandidates);
      if (res.numOfNoEmailCandidate > 0 || res.numOfBlockedCandidates > 0) {
        setShowCheckEligibilityModal(true);
      } else {
        callSendEmailMutate();
      }
    },
    [
      callSendEmailMutate,
      setShowCheckEligibilityModal,
      setNumOfNoEmailCandidates,
      setNumOfBlockedCandidates,
    ]
  );

  const emailEligibilityMutation = candidateService.iexamCreate.opadmin.usePostCandidateCheckEmailEligibility(
    (res: AxiosResponse<CreateCheckEmailEligibilityResponse>) => {
      checkEmailEligibilityCallback(res.data);
    },
    mutateFail
  );

  const {
    mutate: emailEligibilityMutate,
    isLoading: emailEligibilityLoading,
  } = emailEligibilityMutation;
  const callCheckEmailMutate = (
    type: 'ALL' | 'SELECTED' | 'INDIVIDUAL',
    inputRequest: RawCandidateFunctionRequest
  ) => {
    setTotalNumberOfCandidates(inputRequest.uuid === undefined ? 0 : inputRequest.uuid.length);
    setEmailRequest(inputRequest);
    setModalType(type);
    emailEligibilityMutate(inputRequest);
  };

  return {
    numOfNoEmailCandidates,
    numOfBlockedCandidates,
    showCheckEligibilityModal,
    setShowCheckEligibilityModal,
    disabledEmailButton:
      emailLoading ||
      emailEligibilityLoading ||
      showCheckEligibilityModal ||
      data === undefined ||
      data.data.totalElements === 0,
    modalType,
    callCheckEmailMutate,
    callSendEmailMutate,
  };
};

const useExportDownloadAction = (
  data: AxiosResponse<PaginatedData<RawCandidateItemResponse>> | undefined
) => {
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogExportState, setDialogExportState] = useState<boolean>(false);

  const [fileUUID, setFileUUID] = useState('');

  const {
    mutate: exportMutate,
  } = candidateService.iexamCreate.opadmin.usePostCandidateCreateExportExcel(
    (response) => {
      setFileUUID(response.data.uuid);
    },
    (error) => null
  );

  /** to define bulk download
   * */
  const onHandleExport = (inputRequest: RawCandidateFunctionRequest) => {
    setDialogExportState(true);
    exportMutate(inputRequest);
  };

  /** handle download action
   * */
  const downloadCSV = (downloadResponse: RetrieveS3URLResponse) => {
    setDialogExportState(true);
    if (downloadResponse.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogExportState(false);
    }
    setFileDownloadStatus(downloadResponse.fileStatus as FileUploadState);
  };

  /** handle dialog close
   * */
  const onDialogExportClose = () => {
    setDialogExportState(false);
    setFileUUID('');
  };

  return {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton: dialogExportState || data === undefined || data.data.totalElements === 0,
  };
};

export const IExamCandidateCreate = () => {
  const {
    state,
    dispatch: tableDispatch,
    tOptions,
    selectionToolbar,
    remoteTotalRowsSelected,
  } = useDataTable();

  const useDataTableForDataTable = {
    state,
    tableDispatch,
    tOptions,
    selectionToolbar,
    remoteTotalRowsSelected,
  };
  const useCandidateCreateApiDataForDataTable = useCandidateCreateAPIs(state, tableDispatch);

  const {
    _refetch,
    numOfNoEmailCandidates,
    numOfBlockedCandidates,
    showCheckEligibilityModal,
    setShowCheckEligibilityModal,
    callSendEmailMutate,
    modalType,
    dialogExportState,
    fileDownloadStatus,
    onDialogExportClose,
  } = useCandidateCreateApiDataForDataTable;

  const [editDetail, setEditDetail] = useState<any[]>([]);
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  const { data: authorities } = useGetUserAuthorities();

  const hasCreateAccess =
    authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_BULK_CANDIDATE_CREATION_CREATE) ?? false;

  const hasEditAccess =
    authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_BULK_CANDIDATE_CREATION_EDIT) ?? false;

  const handleClose = (doRefetch = false) => {
    setOpen(false);

    if (doRefetch) {
      _refetch();
    }
  };

  const handleCheckEmailConfirm = () => {
    callSendEmailMutate();
  };

  const handleCheckEmailClose = () => {
    setShowCheckEligibilityModal(false);
  };

  const { classes: tableClasses } = useStyles();

  return (
    <>
      <ContentTemplate type="full">
        <SectionHeader title={t('admin->>IExamCandidateCreate.Candidate Profile List')} />
        <Grid xs={12}>
          <Box className={tableClasses.searchContainer}>
            <SearchBar
              className={tableClasses.searchInput}
              placeholder="Search by Name/NRIC/FIN/Passport No./Index No."
              name="searchInput"
              ariaLabel="searchInput"
              defaultValue={state.search}
              searchButton={false}
              onSearch={(searchStr: string) => {
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: searchStr,
                });
              }}
            />
          </Box>
        </Grid>
        <Box id="iexam-candidate-create-table">
          <IExamCandidateCreateDataTable
            setEditDetail={setEditDetail}
            setOpen={setOpen}
            useDataTableForDataTable={useDataTableForDataTable}
            useCandidateCreateApiDataForDataTable={useCandidateCreateApiDataForDataTable}
            hasCreateAccess={hasCreateAccess}
            hasEditAccess={hasEditAccess}
          />
        </Box>
        {open ? (
          <EditDetailsModal
            modalState={open}
            onDialogClose={handleClose}
            details={{
              authentication: editDetail[7] === 'CP_ID' ? 'USERNAME' : editDetail[7],
              email: editDetail[5],
            }}
            uuid={editDetail[12]}
            candidateId={editDetail[0]}
          />
        ) : null}
        {showCheckEligibilityModal ? (
          <IExamCheckEmailModal
            modalType={modalType}
            modalState={showCheckEligibilityModal}
            numOfBlockedCandidates={numOfBlockedCandidates}
            numOfNoEmailCandidates={numOfNoEmailCandidates}
            onDialogClose={handleCheckEmailClose}
            onDialogConfirm={handleCheckEmailConfirm}
          />
        ) : null}
        <DownloadAuditProgressModal
          fileDownloadStatus={fileDownloadStatus}
          modalState={dialogExportState}
          onDialogClose={onDialogExportClose}
        />
      </ContentTemplate>
    </>
  );
};
